import { Outlet, useParams } from 'react-router-dom';
import { SwitcherModal } from '@kinesis/spotlight-ui';
import { useSessionQuery, useLogoutMutation } from '@/api/sessions';
import { useFlag } from '@/hooks/use-flag';
import { useIsEditor } from '@/hooks/use-is-editor';
import { AppLayout } from '@/components/app-layout';
import { useDispatch, useSelector } from 'react-redux';
import { flagsSelector, toggleFlag, useGetFlagsQuery } from '@/api/flags';
import {
  useRecentEntitiesQuery,
  useSearchEntitiesQuery,
  useEntityTypesQuery,
  getEntityUrl,
  getEntityTypeUrl,
} from '@/api/entities';
import { useCallback, useState } from 'react';
import { isEmpty, isNil } from 'lodash/fp';
import { Toaster, Header, Frame } from '@kinesis/bungle';
import { InventoryToolbar } from '@/components/inventory-toolbar';
import { ToolbarPathways } from '@/pages/toolbar-pathways';
import { ToolbarPathway, ToolbarPathwayCrumb } from '@/pages/toolbar-pathway';
import { ToolbarPathwayScenarios } from '@/pages/toolbar-pathway-scenarios';
import { ToolbarPathwayScenario } from '@/pages/toolbar-scenario';
import { ToolbarGpc } from '@/pages/toolbar-gpc';
import { ToolbarDisclosures } from '@/pages/toolbar-disclosures';
import { ToolbarDisclosure } from '@/pages/toolbar-disclosure';
import { ToolbarScenario } from '@/pages/toolbar-scenarios';
import { useGetInventoryQuery } from '@/api/inventories';
import { useNavSelection } from '@/hooks/use-nav-selection';
import Highcharts from 'highcharts';
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';

// The breadcrumb root crumb (label + relative link) for each top-level community
// nav destination, mirroring suit's `getCurrentNav`/`getCurrentNavLink` — every
// nav selection maps to a crumb so the root always renders. `inventory` is the
// promoted Home landing (its link is the bare community index). `baseline`/`gpc`
// keep their entries though the nav items are retired: those routes stay
// URL-reachable (the legacy GPC report is linked from the Disclosures list), so
// their breadcrumb must still render. Deeper segments (an open scenario's name)
// are the `breadcrumbTail` below. The link is relative to the App layout route
// (`/communities/:id`), so e.g. `'scenarios'` → `/communities/:id/scenarios`.
const getCurrentNav = (
  navSelection:
    | 'inventory'
    | 'pathways'
    | 'pathway-scenarios'
    | 'baseline'
    | 'gpc'
    | 'scenarios'
    | 'disclosures',
) =>
  ({
    inventory: 'Inventory',
    pathways: 'Pathways',
    // The legacy surface, reachable from the Pathways list's pinned entry
    // (no nav item of its own — like `baseline`/`gpc` it stays
    // URL-reachable, so its breadcrumb must still render).
    'pathway-scenarios': 'Legacy pathways',
    baseline: 'Baseline',
    gpc: 'Reporting',
    scenarios: 'Scenarios',
    disclosures: 'Disclosures',
  })[navSelection];

const getCurrentNavLink = (
  navSelection:
    | 'inventory'
    | 'pathways'
    | 'pathway-scenarios'
    | 'baseline'
    | 'gpc'
    | 'scenarios'
    | 'disclosures',
) =>
  ({
    inventory: '.',
    pathways: 'pathways',
    'pathway-scenarios': 'pathway-scenarios',
    baseline: 'baseline',
    gpc: 'gpc',
    scenarios: 'scenarios',
    disclosures: 'disclosures',
  })[navSelection];

type InventoryUrl = {
  inventoryId: string;
  pathwayId?: string;
  scenarioId?: string;
  disclosureId?: string;
};

const App = () => {
  NoDataToDisplay(Highcharts);
  const dispatch = useDispatch();
  const { data: session } = useSessionQuery();
  const [logout] = useLogoutMutation();
  const { isSuccess: flagsFetched } = useGetFlagsQuery();
  const enableFlags = useFlag('app.flag-management');
  const flags = useSelector(flagsSelector);
  const [entitySearch, setEntitySearch] = useState('');
  const { data: searchResponse } = useSearchEntitiesQuery(entitySearch, {
    skip: isEmpty(entitySearch),
  });
  const { data: entityTypesResponse } = useEntityTypesQuery(undefined, {
    skip: !flagsFetched,
  });
  const { data: recentEntitiesResponse } = useRecentEntitiesQuery(undefined, {
    skip: !flagsFetched,
  });
  const [switcherModal, setSwitcherModal] = useState(false);
  const onOpenSwitcherModal = useCallback(() => {
    setSwitcherModal(true);
  }, [setSwitcherModal]);

  const onCloseSwitcherModal = useCallback(() => {
    setSwitcherModal(false);
    setEntitySearch('');
  }, [setEntitySearch, setSwitcherModal]);

  const onToggleFlag = useCallback(
    (name: string) => {
      dispatch(toggleFlag(name) as any);
    },
    [dispatch],
  );

  const searchEntities = isEmpty(entitySearch) ? [] : searchResponse?.results;
  const recentEntities = recentEntitiesResponse || [];
  const entityTypes = entityTypesResponse || [];

  const navSelection = useNavSelection();
  const params = useParams<InventoryUrl>();
  const inventoryId = Number(params.inventoryId);
  const pathwayId = params && params.pathwayId && Number(params.pathwayId);
  const scenarioId = params && params.scenarioId && Number(params.scenarioId);
  const disclosureId =
    params && params.disclosureId && Number(params.disclosureId);

  const isEditor = useIsEditor();
  const { data: inventory } = useGetInventoryQuery(inventoryId);

  return (
    <AppLayout
      inventoryId={inventoryId}
      flags={flags}
      session={session}
      enableFlags={enableFlags}
      onToggleFlag={onToggleFlag}
      onLogout={logout}
      onOpenSwitcher={onOpenSwitcherModal}
      inventory={inventory}
    >
      <>
        <Header border shadow>
          <InventoryToolbar
            isEditor={isEditor}
            currentNav={getCurrentNav(navSelection)}
            currentNavLink={getCurrentNavLink(navSelection)}
            breadcrumbTail={
              (!isNil(pathwayId) && navSelection === 'pathways' && (
                <ToolbarPathwayCrumb />
              )) ||
              (!isNil(scenarioId) &&
                (navSelection === 'pathway-scenarios' ? (
                  <ToolbarPathwayScenario />
                ) : navSelection === 'scenarios' ? (
                  <ToolbarScenario />
                ) : null))
            }
          >
            {navSelection === 'pathways' && isNil(pathwayId) && (
              <ToolbarPathways />
            )}
            {navSelection === 'pathways' && !isNil(pathwayId) && (
              <ToolbarPathway />
            )}
            {navSelection === 'pathway-scenarios' && isNil(scenarioId) && (
              <ToolbarPathwayScenarios />
            )}
            {navSelection === 'gpc' && <ToolbarGpc />}
            {navSelection === 'disclosures' && isNil(disclosureId) && (
              <ToolbarDisclosures />
            )}
            {navSelection === 'disclosures' && !isNil(disclosureId) && (
              <ToolbarDisclosure />
            )}
          </InventoryToolbar>
          <Toaster id='globalTop' placement='top' />
        </Header>
        <Frame>
          <Outlet />
        </Frame>
        {switcherModal && (
          <SwitcherModal
            width={496}
            height={600}
            minHeight={320}
            onClose={onCloseSwitcherModal}
            onSearch={setEntitySearch}
            entityTypeList={entityTypes}
            searchResultList={searchEntities}
            recentList={recentEntities}
            getEntityUrl={getEntityUrl}
            getEntityTypeUrl={getEntityTypeUrl}
          />
        )}
      </>
    </AppLayout>
  );
};

export { App };
